import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type ImportSummary } from '../api/client'
import { invalidateFleet } from '../api/invalidate'
import '../components/ComplianceEditor.css'
import './AddVehicle.css'
import './ImportScreen.css'

/** Matches RowOutcome in the API. */
const Outcome = {
  Imported: 1,
  ImportedWithWarnings: 2,
  Rejected: 3,
  SkippedMachinery: 4,
  Merged: 5,
} as const

const OUTCOME_LABEL: Record<number, string> = {
  [Outcome.Imported]: 'Imported',
  [Outcome.ImportedWithWarnings]: 'Imported with warnings',
  [Outcome.Rejected]: 'Rejected',
  [Outcome.SkippedMachinery]: 'Skipped — machinery',
  [Outcome.Merged]: 'Merged duplicate',
}

const OUTCOME_TONE: Record<number, string> = {
  [Outcome.Imported]: 'ok',
  [Outcome.ImportedWithWarnings]: 'warn',
  [Outcome.Rejected]: 'crit',
  [Outcome.SkippedMachinery]: 'none',
  [Outcome.Merged]: 'accent',
}

export function ImportScreen() {
  const navigate = useNavigate()
  const onClose = () => navigate('/register')

  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [summary, setSummary] = useState<ImportSummary | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()


  const upload = useMutation({
    mutationFn: (f: File) => api.importWorkbook(f),
    onSuccess: async (result) => {
      setSummary(result)
      await invalidateFleet(queryClient)
    },
  })

  const report = useQuery({
    queryKey: ['import-report', summary?.importBatchId],
    queryFn: () => api.getImportReport(summary!.importBatchId!),
    enabled: summary?.importBatchId != null,
  })

  function accept(files: FileList | null) {
    const picked = files?.[0]
    if (!picked) return

    // Checked here as well as server-side: a clear message beats a round trip
    // that ends in a 400 the user has to interpret.
    if (!picked.name.toLowerCase().endsWith('.xlsx')) {
      alert('Only .xlsx workbooks are supported.')
      return
    }

    setFile(picked)
    setSummary(null)
  }

  return (
    <div className="formpage">
      <header className="formpage__head">
        <button type="button" className="formpage__back" onClick={onClose}>
          ← Register
        </button>
        <div>
          <div className="eyebrow">Import</div>
          <h1>Upload a fleet workbook</h1>
        </div>
      </header>

      <div className="formpage__body">
          {!summary && (
            <>
              <div
                className={`drop ${dragging ? 'drop--over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragging(false)
                  accept(e.dataTransfer.files)
                }}
                onClick={() => inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={(e) => accept(e.target.files)}
                />
                {file ? (
                  <>
                    <b className="mono">{file.name}</b>
                    <span className="muted">{(file.size / 1024 / 1024).toFixed(1)} MB — ready</span>
                  </>
                ) : (
                  <>
                    <b>Drop an .xlsx file here</b>
                    <span className="muted">or click to choose one</span>
                  </>
                )}
              </div>

              <ul className="rules">
                <li>Reads the <b>VEHICLE</b> sheet.</li>
                <li>
                  One unreadable date never rejects the file — every row gets its own
                  outcome.
                </li>
                <li>Machinery is skipped and duplicates merged, both listed in the report.</li>
                <li>
                  <b>Values you corrected by hand are never overwritten.</b>
                </li>
              </ul>

              {upload.error && (
                <p className="editor__error" role="alert">
                  {(upload.error as Error).message}
                </p>
              )}

              <div className="editor__actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!file || upload.isPending}
                  onClick={() => file && upload.mutate(file)}
                >
                  {upload.isPending ? 'Importing…' : 'Import'}
                </button>
                <button type="button" className="btn" onClick={onClose} disabled={upload.isPending}>
                  Cancel
                </button>
              </div>
            </>
          )}

          {summary && (
            <>
              <div className="tallies">
                <Tally label="Source rows" value={summary.totalSourceRows} />
                <Tally label="Created" value={summary.created} tone="ok" />
                <Tally label="Updated" value={summary.updated} />
                <Tally label="Skipped" value={summary.skippedMachinery} tone="none" />
                <Tally label="Merged" value={summary.merged} tone="accent" />
                <Tally label="Rejected" value={summary.rejected} tone="crit" />
              </div>

              {/* The number that matters most: values NOT overwritten because a
                  person had corrected them. Silence here would look like success
                  while quietly telling the user nothing about what it protected. */}
              {(summary.complianceValuesSkipped ?? 0) > 0 && (
                <p className="protected">
                  <b>{summary.complianceValuesSkipped}</b> hand-corrected values were left
                  untouched — the spreadsheet did not overwrite them.
                </p>
              )}

              <h3 className="report__title">
                Per-row report
                <span className="muted"> — every source row is listed</span>
              </h3>

              {report.isPending && <p className="muted">Loading report…</p>}

              <div className="report">
                {report.data
                  ?.filter((r) => r.outcome !== Outcome.Imported)
                  .map((row) => (
                    <div key={row.sourceRowNumber} className="report__row">
                      <span className={`tag tag--${OUTCOME_TONE[row.outcome ?? 1]}`}>
                        {OUTCOME_LABEL[row.outcome ?? 1]}
                      </span>
                      <span className="mono report__reg">
                        row {row.sourceRowNumber} · {row.registrationRaw ?? '—'}
                      </span>
                      {row.reason && <span className="report__reason">{row.reason}</span>}
                    </div>
                  ))}

                {report.data && report.data.filter((r) => r.outcome !== Outcome.Imported).length === 0 && (
                  <p className="muted">Every row imported cleanly.</p>
                )}
              </div>

              <div className="editor__actions">
                <button type="button" className="btn btn--primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </>
          )}
      </div>
    </div>
  )
}

function Tally({ label, value, tone }: { label: string; value?: number; tone?: string }) {
  return (
    <div className="tally">
      <span className="eyebrow">{label}</span>
      <b className={`mono ${tone ? `tally--${tone}` : ''}`}>{value ?? 0}</b>
    </div>
  )
}
